import { Container, Hex, SelectListItemRenderer, Stroke } from '@acies/core';
import { ModelPosition, TextPosition } from '@acies/domain';
import { Icon, InputField, InputPopupSelector, Label, theme } from '@acies/ui';

/** The PositionItemRenderer is responsible for displaying the item property visually inside the parent PositionsList. */
export default class PositionItemRenderer extends SelectListItemRenderer<ModelPosition | TextPosition> {
	#pos: Label;
	#posIcon: Icon;
	#posIconContainer: Container;
	#text: Label;
	#model: InputPopupSelector;
	#description: Label;
	#widthInput: InputField;
	#heightInput: InputField;
	#quantity: InputField;
	#price: Label;
	#focusStroke: Stroke;
	public constructor() {
		super();
		this.alignment = 'left';
		this.cornerRadius = 8;
		this.cursor = 'pointer';
		this.gapHorizontal = 8;
		this.paddingHorizontal = 16;
		this.width = 'fill';
		// TODO:Martin temporary stroke indicator that will be implemented correctly in another task.
		this.#focusStroke = new Stroke(new Hex('#47C2FF'), 2, 'outside');
	}

	/** Initialize() is called once, the first time the component is added as a Node child, so we create our children here, since we can't do it in the constructor because Angular runs the constructor without access to the DOM api's, so adding node children will fail. */
	protected override initialize(): void {
		super.initialize();
		this.#createChildren();
	}

	/** When the SelectList creates the ItemRenderer children and add them as children, the SelectList sets the item property to the item in the data Array. We can then override the itemChanged() to update the ITemRenderer components display data. */
	protected override itemChanged(): void {
		super.itemChanged();
		this.#setChildrenVisibilityFromItem(this.item);
		this.#updateChildrenPropertiesFromItem(this.item);
	}

	/** The PositionItemRenderer extends InteractiveContainer that has state so we can react to these changes, by overriding the stateChanged() method and in this case, show / hide focus indication. */
	protected override stateChanged(): void {
		super.stateChanged();
		if (this.showFocus) {
			this.fill = theme.colors.fill.focus;
			this.stroke = this.#focusStroke;
		} else if (this.hideFocus) {
			this.fill = null;
			this.stroke = null;
		}
	}

	/** Pos displays the Position index if it's a top level Position, it will be hidden if the item has a parent Position. */
	private get pos(): Label {
		if (this.#pos === undefined) {
			this.#pos = new Label();
			this.#pos.variant = 'dark';
			this.#pos.width = 40;
		}
		return this.#pos;
	}

	/**
	 * Model displays the model property inside a InputPopupSelector, so we can change it if we want to.
	 *
	 * Model will only be displayed if the Position is a ModelPosition.
	 */
	private get model(): InputPopupSelector {
		if (this.#model === undefined) {
			this.#model = new InputPopupSelector();
			this.#model.width = 168;
		}
		return this.#model;
	}

	/** PosIcon shows a "sub" icon only if the item has a Position as parent value.. */
	private get posIcon(): Icon {
		if (this.#posIcon === undefined) {
			this.#posIcon = new Icon('corner-down-right');
		}
		return this.#posIcon;
	}

	/** The icon column is 40 pixels wide, so we place the icon inside a container. */
	private get posIconContainer(): Container {
		if (this.#posIconContainer === undefined) {
			this.#posIconContainer = new Container();
			this.#posIconContainer.width = 40;
			this.#posIconContainer.addComponent(this.posIcon);
		}
		return this.#posIconContainer;
	}

	/** Text displays the item.text property, if the item is a TextPosition. */
	private get text(): Label {
		if (this.#text === undefined) {
			this.#text = new Label();
			this.#text.width = 168;
		}
		return this.#text;
	}

	/** Description displays the item.description property, of all positions if it's not null. */
	private get description(): Label {
		if (this.#description === undefined) {
			this.#description = new Label();
			this.#description.width = 'fill';
		}
		return this.#description;
	}

	/** WidthInput displays the item.width property, if the item is a ModelPosition. */
	private get widthInput(): InputField {
		if (this.#widthInput === undefined) {
			this.#widthInput = new InputField();
			this.#widthInput.type = 'numeric';
			this.#widthInput.textAlign = 'right';
			this.#widthInput.width = 72;
		}
		return this.#widthInput;
	}

	/** HeightInput displays the item.height property, if the item is a ModelPosition. */
	private get heightInput(): InputField {
		if (this.#heightInput === undefined) {
			this.#heightInput = new InputField();
			this.#heightInput.type = 'numeric';
			this.#heightInput.textAlign = 'right';
			this.#heightInput.width = 72;
		}
		return this.#heightInput;
	}

	/** Quantity displays the item.quantity property that will allways be at least 1. */
	private get quantity(): InputField {
		if (this.#quantity === undefined) {
			this.#quantity = new InputField();
			this.#quantity.type = 'numeric';
			this.#quantity.textAlign = 'right';
			this.#quantity.width = 72;
		}
		return this.#quantity;
	}

	/** Price displays the item.price.totalPrice property that will allways be a non negative number. */
	private get price(): Label {
		if (this.#price === undefined) {
			this.#price = new Label();
			this.#price.width = 128;
			this.#price.textAlignHorizontal = 'right';
			this.#price.variant = 'dark';
		}
		return this.#price;
	}

	/** Asked of the styled state, so hovering shows nothing: the cursor is what says the row can be acted on, and a row blurred under the pointer loses its focus look. */
	private get showFocus(): boolean {
		return this.styledState === 'focused';
	}

	private get hideFocus(): boolean {
		return this.styledState === 'idle' || this.styledState === 'pressed';
	}

	private isModelPosition(item: ModelPosition | TextPosition): boolean {
		return item instanceof ModelPosition;
	}

	private isTextPosition(item: ModelPosition | TextPosition): boolean {
		return item instanceof TextPosition;
	}

	#createChildren(): void {
		this.addComponents([this.pos, this.posIconContainer, this.text, this.model, this.description, this.widthInput, this.heightInput, this.quantity, this.price]);
	}

	#setChildrenVisibilityFromItem(item: ModelPosition | TextPosition): void {
		this.pos.visible = item.parent === null;
		this.posIconContainer.visible = item.parent !== null;
		this.text.visible = this.isTextPosition(item);
		this.model.visible = this.isModelPosition(item);
		this.widthInput.visibility = this.isModelPosition(item);
		this.heightInput.visibility = this.isModelPosition(item);
	}

	#updateChildrenPropertiesFromItem(item: ModelPosition | TextPosition): void {
		this.#setSharedPositionProperties(item);
		if (item instanceof ModelPosition) {
			this.model.text = item.model.value;
			this.widthInput.text = String(item.width.value);
			this.heightInput.text = String(item.height.value);
		} else if (item instanceof TextPosition) {
			this.text.text = item.text.value;
		}
	}

	#getDescriptionText(item: ModelPosition | TextPosition): string {
		return item.description !== null ? item.description.value : '';
	}

	#setSharedPositionProperties(item: ModelPosition | TextPosition): void {
		this.pos.text = String(item.index.value);
		this.description.text = this.#getDescriptionText(item);
		this.quantity.text = String(item.quantity.value);
		this.price.text = String(item.price.salesPriceFormatted);
	}
}
customElements.define('position-item-renderer', PositionItemRenderer);
